/**
 * @file Leads service — CRUD operations on leads store (pbf_leads)
 * Compatible with Deploy Z lead data model for bidirectional sync.
 */

import { store, load } from '@state/store.js';

// =============================================
// CONSTANTS
// =============================================

export const STATUS_OPTIONS = {
  'new':        { label: 'New',           color: 'badge-blue',   priority: 5 },
  'favorite':   { label: 'Favorite',      color: 'badge-gold',   priority: 1 },
  'good_fit':   { label: 'Good Fit',      color: 'badge-green',  priority: 2 },
  'wants_book': { label: 'Wants to Book', color: 'badge-purple', priority: 3 },
  'follow_up':  { label: 'Follow Up',     color: 'badge-orange', priority: 4 },
  'contacted':  { label: 'Contacted',     color: 'badge-gold',   priority: 4 },
  'quoted':     { label: 'Quoted',        color: 'badge-purple', priority: 5 },
  'budget':     { label: 'Budget',        color: 'badge-orange', priority: 7 },
  'commercial': { label: 'Commercial',    color: 'badge-blue',   priority: 6 },
  'recurring':  { label: 'Recurring',     color: 'badge-green',  priority: 3 },
  'won':        { label: 'Won',           color: 'badge-green',  priority: 1 },
  'cold':       { label: 'Cold',          color: 'badge-gray',   priority: 8 },
  'dead':       { label: 'Dead',          color: 'badge-red',    priority: 9 },
  'lost':       { label: 'Lost',          color: 'badge-red',    priority: 9 },
};

export const ACTION_OPTIONS = [
  { key: 'picked_up',      label: 'Picked up phone' },
  { key: 'sent_text',      label: 'Sent text' },
  { key: 'sent_card',      label: 'Sent business card' },
  { key: 'booked',         label: 'Booked work' },
  { key: 'sent_estimate',  label: 'Sent estimate' },
  { key: 'left_voicemail', label: 'Left voicemail' },
  { key: 'dmd_social',     label: "DM'd social" },
  { key: 'emailed',        label: 'Emailed' },
  { key: 'met_in_person',  label: 'Met in person' },
  { key: 'sent_flyer',     label: 'Sent flyer' },
];

export const SOURCE_OPTIONS = [
  'Google', 'Facebook', 'Thumbtack', 'Angi', 'Referral',
  'Website', 'Cold Call', 'Nextdoor', 'Door Knock', 'Other'
];

// =============================================
// CRUD OPERATIONS
// =============================================

/**
 * Load all leads from store.
 */
export function getLeads() {
  return load('leads', []);
}

/**
 * Get a single lead by ID.
 */
export function getLeadById(id) {
  const leads = getLeads();
  return leads.find(l => l.id === id) || null;
}

/**
 * Add a new lead.
 */
export function addLead(data) {
  const leads = getLeads();
  const newLead = {
    id: 'lead_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
    name: data.name || '',
    phone: data.phone || '',
    email: data.email || '',
    address: data.address || '',
    source: data.source || '',
    sourceDetail: data.sourceDetail || '',
    status: data.status || 'new',
    value: data.value || 0,
    notes: data.notes || '',
    actions: data.actions || [],
    vibe: data.vibe || 'none',
    inPipeline: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    lastContacted: null,
    // Field Mode activity log
    activityLog: [],
  };
  leads.push(newLead);
  store('leads', leads);
  return newLead;
}

/**
 * Update a lead with partial data.
 */
export function updateLead(id, updates) {
  const leads = getLeads();
  const idx = leads.findIndex(l => l.id === id);
  if (idx === -1) return null;
  leads[idx] = { ...leads[idx], ...updates, updatedAt: new Date().toISOString() };
  store('leads', leads);
  return leads[idx];
}

/**
 * Delete a lead by ID.
 */
export function deleteLead(id) {
  let leads = getLeads();
  leads = leads.filter(l => l.id !== id);
  store('leads', leads);
}

/**
 * Update lead status and log activity.
 */
export function updateLeadStatus(id, newStatus) {
  const leads = getLeads();
  const lead = leads.find(l => l.id === id);
  if (!lead) return null;
  const oldStatus = lead.status;
  lead.status = newStatus;
  lead.updatedAt = new Date().toISOString();
  // Log activity
  if (!lead.activityLog) lead.activityLog = [];
  lead.activityLog.push({
    type: 'status_change',
    from: oldStatus,
    to: newStatus,
    at: new Date().toISOString(),
  });
  store('leads', leads);
  return lead;
}

/**
 * Add a timestamped note to a lead.
 */
export function addLeadNote(id, text, type = 'general') {
  const leads = getLeads();
  const lead = leads.find(l => l.id === id);
  if (!lead) return null;

  // Handle both string notes (Deploy Z) and our activity log
  // Keep the main notes field as a string for Deploy Z compat
  const timestamp = new Date().toISOString();
  const prefix = `[${new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}] `;

  if (typeof lead.notes === 'string') {
    lead.notes = lead.notes ? lead.notes + '\n' + prefix + text : prefix + text;
  } else {
    lead.notes = prefix + text;
  }

  lead.updatedAt = timestamp;
  lead.lastContacted = timestamp;

  // Also add to activity log
  if (!lead.activityLog) lead.activityLog = [];
  lead.activityLog.push({
    type: 'note',
    noteType: type,
    text: text,
    at: timestamp,
  });

  store('leads', leads);
  return lead;
}

/**
 * Toggle a lead action (matches Deploy Z model where actions is array of strings).
 */
export function toggleLeadAction(id, actionKey) {
  const leads = getLeads();
  const lead = leads.find(l => l.id === id);
  if (!lead) return null;
  if (!lead.actions) lead.actions = [];

  const idx = lead.actions.indexOf(actionKey);
  const now = new Date().toISOString();

  if (idx >= 0) {
    lead.actions.splice(idx, 1);
  } else {
    lead.actions.push(actionKey);
    lead.lastContacted = now;
  }

  lead.updatedAt = now;

  // Log activity
  if (!lead.activityLog) lead.activityLog = [];
  lead.activityLog.push({
    type: 'action_toggle',
    action: actionKey,
    done: idx < 0,
    at: now,
  });

  store('leads', leads);
  return lead;
}

/**
 * Move lead to Sales Pipeline (Deploy Z compatible).
 */
export function moveLeadToPipeline(id) {
  const leads = getLeads();
  const lead = leads.find(l => l.id === id);
  if (!lead) return false;

  const pipelineData = load('pipeline', {});
  const stage = 'New Lead';
  if (!pipelineData[stage]) pipelineData[stage] = [];

  // Build card
  const actionLabels = [];
  if (lead.actions && lead.actions.length > 0) {
    lead.actions.forEach(ak => {
      const opt = ACTION_OPTIONS.find(a => a.key === ak);
      if (opt) actionLabels.push(opt.label);
    });
  }

  let notesSummary = lead.notes || '';
  if (actionLabels.length > 0) notesSummary += '\n\nActions taken: ' + actionLabels.join(', ');

  const card = {
    id: 'card_' + Date.now(),
    name: lead.name || '',
    phone: lead.phone || '',
    email: lead.email || '',
    address: lead.address || '',
    source: (lead.source || 'Unknown') + ' \u2192 Leads Tab',
    notes: notesSummary.trim(),
    amount: lead.value ? String(lead.value) : '',
    createdAt: new Date().toISOString(),
  };

  pipelineData[stage].push(card);
  store('pipeline', pipelineData);

  // Mark lead as in pipeline
  lead.inPipeline = true;
  lead.updatedAt = new Date().toISOString();
  store('leads', leads);

  return true;
}

/**
 * Search leads by query string.
 */
export function searchLeads(query) {
  if (!query) return getLeads();
  const q = query.toLowerCase();
  return getLeads().filter(l =>
    (l.name || '').toLowerCase().includes(q) ||
    (l.phone || '').toLowerCase().includes(q) ||
    (l.email || '').toLowerCase().includes(q) ||
    (l.notes || '').toLowerCase().includes(q) ||
    (l.source || '').toLowerCase().includes(q)
  );
}

/**
 * Get leads filtered by status.
 */
export function getLeadsByStatus(status) {
  return getLeads().filter(l => l.status === status);
}

/**
 * Get leads needing follow-up (new or no recent contact).
 */
export function getLeadsNeedingFollowUp() {
  const leads = getLeads();
  const twoDaysAgo = Date.now() - (2 * 24 * 60 * 60 * 1000);
  return leads.filter(l => {
    if (l.status === 'dead' || l.status === 'lost' || l.status === 'won' || l.inPipeline) return false;
    if (l.status === 'new') return true;
    if (l.status === 'follow_up') return true;
    if (!l.lastContacted) return true;
    return new Date(l.lastContacted).getTime() < twoDaysAgo;
  });
}

/**
 * Format a relative time string.
 */
export function timeAgo(dateStr) {
  if (!dateStr) return 'never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
